import React, { useState } from 'react';

import { BookingsSearchForm } from '@components/admin/booking/BookingsSearchForm';
import { BookingsTable } from '@components/admin/booking/BookingsTable';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { ExportCSVDialog2 } from '@components/ExportCSVDialog2';
import { adminBookingInquiryTradeCondtionFilters } from '@data/adminBookingInquiry';
import { bookingsStrings } from '@data/bookings';
import { useSearch } from '@hooks/next/useSearch';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import { useUser } from '@hooks/queries/useUser';
import { DashboardPreAlertSearchType, type PreAlertType } from '@models/next/PreAlert';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';
import { pick } from 'remeda';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
}));

export const PastBookings: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search } = useSearch(DashboardPreAlertSearchType);

  const { checklist = [], ...preAlertSearch } = search;

  const query = {
    ...preAlertSearch,
    ...(search.trade_condition &&
      adminBookingInquiryTradeCondtionFilters.get(search.trade_condition)),
    page: search.page ?? 1,
    page_size: search.page_size ?? 50,
    type: ['past_finished'] as PreAlertType[],
  };

  const title = bookingsStrings.title()['past_finished'];

  const user = useUser();

  const routePlans = useRoutePlanList({});

  const podOptions = uniq(routePlans.data?.map((routePlan) => routePlan.pod)).map((pod) => ({
    value: pod,
    label: pod,
  }));
  const polOptions = uniq(routePlans.data?.map((routePlan) => routePlan.pol)).map((pol) => ({
    value: pol,
    label: pol,
  }));

  const [UIState, setUIState] = useState(() => ({
    exportDialog: {
      open: false,
    },
  }));

  const showProfit = user.data ? new UserHelper(user.data).isJapanStaff : false;

  return (
    <Layout title={title} loading={user.isLoading} errors={[user.error]}>
      <ExportCSVDialog2
        open={UIState.exportDialog.open}
        onClose={() => {
          setUIState((state) => {
            return { ...state, exportDialog: { open: false } };
          });
        }}
        subject={bookingsStrings.booking}
        title={`${title} ${dayjs().format('YYYY-MM-DD HH:mm')}.csv`}
        baseUrl={'/dashboard/pre-alert/'}
        selected={checklist}
        query={query}
        defaultQuery={pick(query, ['type'])}
      />

      <div className={classes.container}>
        <SectionTitle
          actions={
            <Button
              onClick={() => {
                setUIState((state) => {
                  return { ...state, exportDialog: { open: true } };
                });
              }}
              color="primary"
              text={bookingsStrings.exportCSV}
              shape="round"
            />
          }
        >
          {title}
        </SectionTitle>

        <BookingsSearchForm
          tagFields={[
            'd2d_no',
            'consignee',
            'pol',
            'pod',
            'quantity',
            'member_salesperson',
            'salesperson_japan',
          ]}
          rangeFields={['etd', 'eta', 'delivery_date']}
          fieldsInputOptions={{
            pol: { type: 'choose', options: polOptions },
            pod: { type: 'choose', options: podOptions },
          }}
        />

        <BookingsTable
          queryParams={query}
          columns={[
            'salesperson_japan_last_name',
            'salesperson_china_last_name',
            'delivery_date',
            'agency_name',
            'consignee',
            'etd',
            'eta',
            'vessel',
            'pol',
            'pod',
            'quantity',
            'member_salesperson_last_name',
            'invoice_status',
            ...(showProfit
              ? ([
                  'invoice_tax_excluded_total_price',
                  'payment_tax_excluded_total_price',
                  'profit',
                  'is_ledger_locked',
                ] as const)
              : []),
          ]}
          user={user.data}
          podOptions={podOptions}
          polOptions={polOptions}
          type="past_finished"
        />
      </div>
    </Layout>
  );
};
